import { useMutation, useQueryClient } from "@tanstack/react-query";

import formatSelection from "../../lib/Selection/formatSelection";
import type { UseSelectionReturn } from "../../lib/Selection/useSelection";
import { useConfig } from "../../lib/useConfig";
import useToken from "../../lib/useToken";

export const optimisticUpdateLocation = (
  cart: UseSelectionReturn,
  variables: {
    locationId: string;
    stateId?: string;
  }
) => {
  const currentCountry = cart.countries.find(
    (c: {
      country: string;
      name: string;
      eu: boolean;
      states?: { state: string; name: string }[];
    }) => c?.country === variables.locationId
  );
  const newLocation = {
    country: currentCountry?.country || "",
    name: currentCountry?.name || "",
    eu: currentCountry?.eu || false,
    state: variables.stateId || "",
  };
  return {
    ...cart,
    location: {
      ...cart.location,
      ...newLocation,
    },
    selection: {
      ...cart.selection,
      currency: cart.countries.find(
        (c: { country: string; currency: string }) =>
          c?.country === variables.locationId
      )?.currency,
    },
  };
};
export const setLocation = async (
  variables: {
    locationId: string;
    stateId?: string;
  },
  customerToken: string,
  setToken: (token: string) => void,
  api?: string
) => {
  if (window.CentraCheckout) window.CentraCheckout.suspend();
  const { locationId, stateId } = variables;

  const url = stateId
    ? `${api}/countries/${locationId}/states/${stateId}`
    : `${api}/countries/${locationId}`;

  const res = await fetch(url, {
    method: "PUT",
    headers: { "API-token": customerToken || "" },
  });
  if (!res.ok) throw Error("Error changing location");
  const data = await res.json();
  if (data === null) throw Error("Error changing location");
  setToken(data.token);

  return data;
};

const useChangeLocation = () => {
  const queryClient = useQueryClient();
  const { config } = useConfig();
  const { token: customerToken, setToken } = useToken();
  const api = config?.centra.url;
  const changeLocation = useMutation({
    mutationFn: async (variables: { locationId: string; stateId?: string }) => {
      await queryClient.cancelQueries({ queryKey: ["selection"] });
      return setLocation(variables, customerToken, setToken, api);
    },

    onMutate: async (variables: { locationId: string; stateId?: string }) => {
      // Snapshot the previous value
      const oldData = queryClient.getQueryData(["selection"]);

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      //eslint-disable-next-line

      // // Optimistically update to the new value
      try {
        await queryClient.setQueryData(["selection"], (oldCart: any) => {
          return optimisticUpdateLocation(oldCart, variables);
        });
      } catch (e) {}
      return { oldData };
    },

    onSuccess: async (data: any) => {
      const res = await queryClient.setQueryData(
        ["selection"],
        formatSelection(data)
      );
      return res;
    },

    onSettled: async () =>
      // data: any,
      // error: any,
      // variables: { locationId: string; stateId?: string },
      // context: any
      {
        return queryClient.invalidateQueries({ queryKey: ["selection"] });
      },
  });

  return { changeLocation };
};

export default useChangeLocation;
